import React, { useState, useEffect } from "react";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import Icon from "../../common/elements/Icon";
import Button from "../../common/elements/Button";
import Select from "react-select";
import { instrumentationSources as instrumentationSourcesOptions, thor_api_urls, reviewTypeMapping } from "../../common/Constants";
import useFetchWithMsal from "../../../useFetchWithMsal";

/**
 * A ReviewTemplateModal component for displaying HTML content with Accept/Reject actions.
 * Fully self-contained with built-in state management and API integration.
 *
 * -- Props --
 * | Name              | Type      | Required  | Default Value | Description
 * |-------------------|-----------|-----------|---------------|---------------
 * | isActive          | boolean   | true      | false         | Whether the modal is visible
 * | title             | string    | false     | ""            | The modal title
 * | content           | string    | false     | ""            | HTML content to display]
 * | onClose           | function  | true      | null          | Function to call when modal is closed
 * | experimentKey     | string    | true      | null          | The experiment key for API calls
 * | experimentState   | string    | false     | ""            | The experiment state to determine button type
 * | showInstrumentationProperties | boolean | false | false | Whether to show instrumentation multi-select
 * | instrumentationInstructions | object | false | {} | Existing instrumentation instructions to prepopulate fields
 * | onSuccess         | function  | false     | null          | Function to call after successful review submission
 */
export const ReviewTemplateModal = (props) => {
    const {
        isActive = false,
        title = "",
        content = "",
        onClose,
        experimentKey,
        experimentState = "",
        showInstrumentationProperties = false,
        instrumentationInstructions = {},
        onSuccess
    } = props;

    const { execute } = useFetchWithMsal();
    
    // Internal state management - fully self-contained
    const [comment, setComment] = useState("");
    const [instrumentationSources, setInstrumentationSources] = useState([]);
    const [impactedServices, setImpactedServices] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);

    // Helper function to convert string arrays to select format
    const convertFromStringArrayToSelectFormat = (input, options) => {
        if (!input || !Array.isArray(input)) {
            return [];
        }
        return input.map(item => {
            const matchingOption = options.find(option => option.value === item);
            return matchingOption || { value: item, label: item };
        });
    };

    // Reset state when modal opens and prepopulate instrumentation fields
    useEffect(() => {
        if (isActive) {
            setComment("");
            setError(null);
            setIsSubmitting(false);
            
            // Prepopulate instrumentation fields if instrumentationInstructions are provided
            if (showInstrumentationProperties && instrumentationInstructions) {
                setInstrumentationSources(
                    convertFromStringArrayToSelectFormat(
                        instrumentationInstructions.instrumentationSources, 
                        instrumentationSourcesOptions
                    )
                );
                setImpactedServices(
                    convertFromStringArrayToSelectFormat(
                        instrumentationInstructions.impactedServices, 
                        instrumentationSourcesOptions
                    )
                );
            } else {
                setInstrumentationSources([]);
                setImpactedServices([]);
            }
        }
    }, [isActive, instrumentationInstructions, showInstrumentationProperties]);

    // Internal handlers
    const handleCommentChange = (value) => {
        setComment(value);
    };

    const handleInstrumentationSourcesChange = (selectedOptions) => {
        setInstrumentationSources(selectedOptions || []);
    };

    const handleImpactedServicesChange = (selectedOptions) => {
        setImpactedServices(selectedOptions || []);
    };

    // API call to submit review
    const submitReview = async (approved, commentText = '') => {
        if (!experimentKey) {
            setError('Experiment key is required');
            return;
        }

        setIsSubmitting(true);
        setError(null);

        try {
            const reviewType = reviewTypeMapping[experimentState?.toLowerCase()] || 'engineer_design_review';
            
            const reviewData = {
                reviewType: reviewType,
                approved: approved,
                comment: commentText
            };

            // Add instrumentation instructions if this is an instrumentation review
            if (showInstrumentationProperties) {
                reviewData.instrumentationInstructions = {};
                if (instrumentationSources.length > 0) {
                    reviewData.instrumentationInstructions.instrumentationSources = instrumentationSources.map(instrument => instrument.value);
                }
                if (impactedServices.length > 0) {
                    reviewData.instrumentationInstructions.impactedServices = impactedServices.map(service => service.value);
                }
            }

            const endpoint = thor_api_urls.experimentReviews(experimentKey);
            console.log('Submitting review:', reviewData);
            
            await execute('POST', endpoint, reviewData);
            console.log('Review submitted successfully');
            
            // Success callback
            if (onSuccess) {
                onSuccess();
            }
            
            // Close modal
            onClose();
            
        } catch (error) {
            console.error('Failed to submit review:', error);
            setError('Failed to submit review. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Enhanced handlers that use internal API call
    const handleAccept = async () => {
        await submitReview(true, comment.trim());
    };

    const handleReject = async () => {
        await submitReview(false, comment.trim());
    };

    const handleSubmit = async () => {
        await submitReview(true, comment.trim());
    };

    if (!isActive) {
        return null;
    }

    return (
        <div className="modal is-active">
            <div className="modal-background" onClick={onClose}></div>
            <div className="modal-card" style={{width: '90%', maxWidth: '800px'}}>
                <header className="modal-card-head">
                    <p className="modal-card-title">{title}</p>
                    <button 
                        className="delete" 
                        aria-label="close"
                        onClick={onClose}
                        disabled={isSubmitting}
                    >
                        <Icon icon={faTimes} />
                    </button>
                </header>
                <div className="modal-card-body">
                    {error && (
                        <div className="notification is-danger">
                            <button className="delete" onClick={() => setError(null)}></button>
                            {error}
                        </div>
                    )}
                    
                    {isSubmitting ? (
                        <div className="has-text-centered">
                            <div className="spinner"></div>
                            <p>Loading content...</p>
                        </div>
                    ) : (
                        <div>
                            <div
                                dangerouslySetInnerHTML={{ __html: content }}
                                className="content"
                            />
                            {showInstrumentationProperties && (
                                <>
                                    <div className="mt-4">
                                        <label className="label">Select Instrumentation Sources:</label>
                                        <Select
                                            isMulti
                                            options={instrumentationSourcesOptions}
                                            value={instrumentationSources}
                                            onChange={handleInstrumentationSourcesChange}
                                            placeholder="Select Instrumentation Sources"
                                            className="basic-multi-select"
                                            classNamePrefix="select"
                                            isDisabled={isSubmitting}
                                        />
                                    </div>
                                    <div className="mt-4">
                                        <label className="label">Select Impacted Services:</label>
                                        <Select
                                            isMulti
                                            options={instrumentationSourcesOptions}
                                            value={impactedServices}
                                            onChange={handleImpactedServicesChange}
                                            placeholder="Select Impacted Services"
                                            className="basic-multi-select"
                                            classNamePrefix="select"
                                            isDisabled={isSubmitting}
                                        />
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </div>
                <footer className="modal-card-foot" style={{flexDirection: 'column', alignItems: 'stretch'}}>
                    <div style={{width: '100%', marginBottom: '15px'}}>
                        <input
                            className="input"
                            type="text"
                            placeholder="Enter the reason for the action..."
                            value={comment}
                            onChange={(e) => handleCommentChange(e.target.value)}
                            disabled={isSubmitting}
                            style={{width: '100%'}}
                        />
                    </div>
                    {experimentState?.toLowerCase() === 'design_review' ? (
                        <div style={{display: 'flex', justifyContent: 'center', gap: '10px'}}>
                            <Button
                                className="button is-danger"
                                onClick={handleReject}
                                disabled={isSubmitting}
                                tooltip={(!comment || !comment.trim()) ? "Please provide a reason to reject." : null}
                            >
                                {isSubmitting ? 'Submitting...' : 'Reject'}
                            </Button>
                            <Button
                                className="button is-success"
                                onClick={handleAccept}
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? 'Submitting...' : 'Accept'}
                            </Button>
                        </div>
                    ) : (
                        <div style={{display: 'flex', justifyContent: 'center'}}>
                            <Button
                                className="button is-primary"
                                onClick={handleSubmit}
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? 'Submitting...' : 'Submit'}
                            </Button>
                        </div>
                    )}
                </footer>
            </div>
        </div>
    );
};

export default ReviewTemplateModal;
